import { Directive, ElementRef, OnInit, inject } from "@angular/core";
import { AppBarProfileButtonComponent, AvatarComponent } from "@tylertech/forge";

class HSL
{
    Hue: number = 231;
    Lightness: number = 99;
    Saturation: number = 62;
    RequiresDarkBackground = false;
}

class AvatarColor
{
    static luminanceThreshold = 0.45;
    static charMin = 65;
    static charMax = 90;
    static hueMin = 0;
    static hueMax = 359;
    static numericMin = 48;
    static numericMax = 57;
    static lightnessMin = 40;
    static lightnessMax = 70;
    static saturationMin = 50;
    static saturationMax = 80;

    static getHsl(hash: number, lastChar: number): HSL
    {
        let hue = 231;
        let lightness = 99;
        let saturation = 62;

        if (hash >= AvatarColor.numericMin && hash <= AvatarColor.numericMax)
        {
            lightness = 80;
            hash = this.convertToRangeValue(hash, AvatarColor.numericMax, AvatarColor.numericMin, AvatarColor.charMax, AvatarColor.charMin);
        }

        if (lastChar >= AvatarColor.numericMin && lastChar <= AvatarColor.numericMax)
        {
            saturation = this.convertToRangeValue(lastChar, AvatarColor.numericMax, AvatarColor.numericMin, AvatarColor.saturationMax, AvatarColor.saturationMin);
            lightness = this.convertToRangeValue(lastChar, AvatarColor.numericMax, AvatarColor.numericMin, AvatarColor.lightnessMax, AvatarColor.lightnessMin);
        }
        else
        {
            saturation = this.convertToRangeValue(lastChar, AvatarColor.charMax, AvatarColor.charMin, AvatarColor.saturationMax, AvatarColor.saturationMin);
            lightness = this.convertToRangeValue(lastChar, AvatarColor.charMax, AvatarColor.charMin, AvatarColor.lightnessMax, AvatarColor.lightnessMin);
        }

        hue = this.convertToRangeValue(hash, AvatarColor.charMax, AvatarColor.charMin, AvatarColor.hueMax, AvatarColor.hueMin);

        const hsl = new HSL();
        hsl.Hue = hue;
        hsl.Saturation = saturation;
        hsl.Lightness = lightness;
        hsl.RequiresDarkBackground = this.hslToLuminance(hue, saturation, lightness) > AvatarColor.luminanceThreshold;

        return hsl;
    }

    private static convertToRangeValue(oldValue: number, oldMax: number, oldMin: number, newMax: number, newMin: number): number
    {
        return Math.round(((oldValue - oldMin) / (oldMax - oldMin)) * (newMax - newMin) + newMin);
    }

    private static hslToLuminance(h: number, s: number, l: number): number
    {
        h /= 360;
        s /= 100;
        l /= 100;

        let r: number, g: number, b: number;

        if (s === 0)
        {
            r = g = b = l;
        }
        else
        {
            function hue2rgb(p: number, q: number, t: number): number
            {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        return luminance;
    }
}

@Directive({
    selector: "forge-avatar",
    standalone: false
})
export class ForgeAvatarColorDirective implements OnInit
{
    private hostElement = inject<ElementRef<AvatarComponent>>(ElementRef);


    ngOnInit()
    {
        const text = this.hostElement.nativeElement.text.trim().toUpperCase();
        const hash = text.charCodeAt(0);
        const lastChar = text.charCodeAt(text.length - 1);

        const hsl = AvatarColor.getHsl(hash, lastChar);

        const background = `hsl(${hsl.Hue}, ${hsl.Saturation}%, ${hsl.Lightness}%)`;
        if (hsl.RequiresDarkBackground)
        {
            this.hostElement.nativeElement.style.setProperty("--forge-avatar-color", "#000000");
        }
        this.hostElement.nativeElement.style.setProperty("--forge-avatar-background", background);
    }
}

@Directive({
    selector: "forge-app-bar-profile-button",
    standalone: false
})
export class ForgeAppBarProfileButtonAvatarColorDirective implements OnInit
{
    private hostElement = inject<ElementRef<AppBarProfileButtonComponent>>(ElementRef);


    ngOnInit()
    {
        const avatar = this.hostElement.nativeElement.querySelector("forge-avatar");
        const text = this.hostElement.nativeElement.avatarText.trim().toUpperCase();
        const hash = text.charCodeAt(0);
        const lastChar = text.charCodeAt(text.length - 1);

        const hsl = AvatarColor.getHsl(hash, lastChar);

        const background = `hsl(${hsl.Hue}, ${hsl.Saturation}%, ${hsl.Lightness}%)`;

        if (hsl.RequiresDarkBackground)
        {
            avatar.style.setProperty("--forge-avatar-color", "#000000");
        }
        avatar.style.setProperty("--forge-avatar-background", background);
    }
}
